import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Repeat, ChevronDown, Check } from "lucide-react";

/**
 * "Repeats" picker — sits below the date row in QuickAddSheet (task-only).
 *
 * Manages a single recurrence value as a normalised object:
 *   { type: "none"|"weekly"|"monthly"|"yearly"|"custom",
 *     interval: int (>=1),
 *     unit?: "day"|"week"|"month"|"year"  // custom only
 *     day?: int,                          // weekday 0-6 or DOM 1-31
 *     month?: int }                       // 1-12, yearly only
 *
 * Helper text (Mochi's voice) renders under the picker once a value is
 * chosen — per spec.
 *
 * Visual style matches the existing date/lead pickers in QuickAddSheet:
 * clean, minimal, surface bg, terracotta on selection. Hard-caps interval
 * at 52 (≈ a year of weekly) to mirror the backend 10-year safety cap
 * without making the UI scroll forever.
 */
const DAYS = [
    { i: 0, label: "Mon" }, { i: 1, label: "Tue" }, { i: 2, label: "Wed" },
    { i: 3, label: "Thu" }, { i: 4, label: "Fri" }, { i: 5, label: "Sat" },
    { i: 6, label: "Sun" },
];
const MONTHS = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const TYPE_OPTIONS = [
    { id: "none",    label: "Never" },
    { id: "weekly",  label: "Every week" },
    { id: "monthly", label: "Every month" },
    { id: "yearly",  label: "Every year" },
    { id: "custom",  label: "Custom" },
];

export default function RepeatPicker({ value, onChange }) {
    const [open, setOpen] = useState(false);
    const rec = value || { type: "none", interval: 1 };
    const isOn = (rec.type || "none") !== "none";

    const summary = (() => {
        if (!isOn) return "never";
        if (rec.type === "weekly") {
            const dayLabel = DAYS.find((d) => d.i === (rec.day ?? 0))?.label || "Mon";
            return rec.interval > 1
                ? `every ${rec.interval} weeks on ${dayLabel}`
                : `every ${dayLabel}`;
        }
        if (rec.type === "monthly") {
            const ord = nth(rec.day ?? 1);
            return rec.interval > 1
                ? `every ${rec.interval} months · the ${ord}`
                : `every month · the ${ord}`;
        }
        if (rec.type === "yearly") {
            const m = MONTHS[(rec.month ?? 1) - 1];
            const ord = nth(rec.day ?? 1);
            return rec.interval > 1
                ? `every ${rec.interval} years · ${m} ${ord}`
                : `every year · ${m} ${ord}`;
        }
        if (rec.type === "custom") {
            const u = rec.unit || "day";
            return `every ${rec.interval || 1} ${u}${(rec.interval || 1) > 1 ? "s" : ""}`;
        }
        return "never";
    })();

    const helper = (() => {
        if (!isOn) return null;
        if (rec.type === "weekly") {
            const d = DAYS.find((x) => x.i === (rec.day ?? 0))?.label || "Mon";
            return `every ${d} — mochi will remind you 🍡`;
        }
        if (rec.type === "monthly") {
            return `every ${nth(rec.day ?? 1)} of the month — on it 📅`;
        }
        if (rec.type === "yearly") {
            const m = MONTHS[(rec.month ?? 1) - 1];
            return `every ${m} ${rec.day ?? 1} — already on mochi's calendar ✓`;
        }
        if (rec.type === "custom") {
            const u = rec.unit || "day";
            return `every ${rec.interval || 1} ${u}${(rec.interval || 1) > 1 ? "s" : ""} — mochi's got the rhythm 🍡`;
        }
        return null;
    })();

    const set = (next) => {
        onChange({ ...rec, ...next });
    };

    return (
        <div className="rounded-2xl bg-surface p-3" data-testid="repeat-picker">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center justify-between gap-2"
                data-testid="repeat-picker-toggle"
                aria-expanded={open}
            >
                <span className="inline-flex items-center gap-2 font-display text-sm text-warm-900">
                    <Repeat size={15} className={isOn ? "text-terracotta" : "text-warm-500"} />
                    <span className="text-xs uppercase tracking-widest text-warm-500">repeats</span>
                    <span className="text-warm-900">· {summary}</span>
                </span>
                <ChevronDown
                    size={16}
                    className={`text-warm-500 transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.18 }}
                        className="overflow-hidden"
                    >
                        <div className="mt-3 grid grid-cols-2 gap-1.5">
                            {TYPE_OPTIONS.map((opt) => {
                                const sel = (rec.type || "none") === opt.id;
                                return (
                                    <button
                                        key={opt.id}
                                        type="button"
                                        onClick={() => {
                                            // Sensible defaults per type.
                                            if (opt.id === "none") onChange({ type: "none", interval: 1 });
                                            else if (opt.id === "weekly")  onChange({ type: "weekly", interval: 1, day: 0 });
                                            else if (opt.id === "monthly") onChange({ type: "monthly", interval: 1, day: 1 });
                                            else if (opt.id === "yearly")  onChange({ type: "yearly", interval: 1, month: 1, day: 1 });
                                            else if (opt.id === "custom")  onChange({ type: "custom", interval: 1, unit: "day" });
                                        }}
                                        className={`inline-flex items-center justify-between gap-1.5 px-3 py-2 rounded-xl text-sm font-display transition-all ${
                                            sel ? "bg-terracotta text-cream" : "bg-cream text-warm-900 hover:bg-cream/80"
                                        }`}
                                        data-testid={`repeat-type-${opt.id}`}
                                    >
                                        {opt.label}
                                        {sel && <Check size={14} />}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Type-specific extras */}
                        {rec.type === "weekly" && (
                            <div className="mt-3">
                                <p className="font-body text-[10px] uppercase tracking-widest text-warm-500 mb-1.5">on which day</p>
                                <div className="flex flex-wrap gap-1.5">
                                    {DAYS.map((d) => {
                                        const sel = rec.day === d.i;
                                        return (
                                            <button
                                                key={d.i}
                                                type="button"
                                                onClick={() => set({ day: d.i })}
                                                className={`px-3 py-1.5 rounded-full text-xs font-display transition-all ${
                                                    sel ? "bg-terracotta text-cream" : "bg-cream text-warm-800"
                                                }`}
                                                data-testid={`repeat-weekly-day-${d.i}`}
                                            >
                                                {d.label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {rec.type === "monthly" && (
                            <div className="mt-3">
                                <p className="font-body text-[10px] uppercase tracking-widest text-warm-500 mb-1.5">day of the month</p>
                                <input
                                    type="number"
                                    min={1}
                                    max={31}
                                    value={rec.day ?? 1}
                                    onChange={(e) => set({ day: clamp(parseInt(e.target.value || "1", 10), 1, 31) })}
                                    className="w-24 bg-cream rounded-2xl px-3 py-2 text-warm-900 outline-none border-2 border-transparent focus:border-terracotta"
                                    data-testid="repeat-monthly-day"
                                />
                                <p className="font-body text-[10px] text-warm-500 mt-1">
                                    short months use the last day automatically.
                                </p>
                            </div>
                        )}

                        {rec.type === "yearly" && (
                            <div className="mt-3 flex gap-3 flex-wrap">
                                <div>
                                    <p className="font-body text-[10px] uppercase tracking-widest text-warm-500 mb-1.5">month</p>
                                    <select
                                        value={rec.month ?? 1}
                                        onChange={(e) => set({ month: parseInt(e.target.value, 10) })}
                                        className="bg-cream rounded-2xl px-3 py-2 text-warm-900 outline-none border-2 border-transparent focus:border-terracotta"
                                        data-testid="repeat-yearly-month"
                                    >
                                        {MONTHS.map((m, i) => (
                                            <option key={m} value={i + 1}>{m}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <p className="font-body text-[10px] uppercase tracking-widest text-warm-500 mb-1.5">day</p>
                                    <input
                                        type="number"
                                        min={1}
                                        max={31}
                                        value={rec.day ?? 1}
                                        onChange={(e) => set({ day: clamp(parseInt(e.target.value || "1", 10), 1, 31) })}
                                        className="w-20 bg-cream rounded-2xl px-3 py-2 text-warm-900 outline-none border-2 border-transparent focus:border-terracotta"
                                        data-testid="repeat-yearly-day"
                                    />
                                </div>
                            </div>
                        )}

                        {rec.type === "custom" && (
                            <div className="mt-3 flex items-end gap-2 flex-wrap">
                                <div>
                                    <p className="font-body text-[10px] uppercase tracking-widest text-warm-500 mb-1.5">every</p>
                                    <input
                                        type="number"
                                        min={1}
                                        max={52}
                                        value={rec.interval ?? 1}
                                        onChange={(e) => set({ interval: clamp(parseInt(e.target.value || "1", 10), 1, 52) })}
                                        className="w-20 bg-cream rounded-2xl px-3 py-2 text-warm-900 outline-none border-2 border-transparent focus:border-terracotta"
                                        data-testid="repeat-custom-interval"
                                    />
                                </div>
                                <div>
                                    <p className="font-body text-[10px] uppercase tracking-widest text-warm-500 mb-1.5">unit</p>
                                    <select
                                        value={rec.unit || "day"}
                                        onChange={(e) => set({ unit: e.target.value })}
                                        className="bg-cream rounded-2xl px-3 py-2 text-warm-900 outline-none border-2 border-transparent focus:border-terracotta"
                                        data-testid="repeat-custom-unit"
                                    >
                                        <option value="day">days</option>
                                        <option value="week">weeks</option>
                                        <option value="month">months</option>
                                        <option value="year">years</option>
                                    </select>
                                </div>
                            </div>
                        )}

                        {/* Optional interval for weekly/monthly/yearly — kept tucked so the
                            common case (every 1) stays one-tap. */}
                        {(rec.type === "weekly" || rec.type === "monthly" || rec.type === "yearly") && (
                            <div className="mt-3">
                                <p className="font-body text-[10px] uppercase tracking-widest text-warm-500 mb-1.5">
                                    every N {rec.type === "weekly" ? "weeks" : rec.type === "monthly" ? "months" : "years"}
                                </p>
                                <input
                                    type="number"
                                    min={1}
                                    max={52}
                                    value={rec.interval ?? 1}
                                    onChange={(e) => set({ interval: clamp(parseInt(e.target.value || "1", 10), 1, 52) })}
                                    className="w-20 bg-cream rounded-2xl px-3 py-2 text-warm-900 outline-none border-2 border-transparent focus:border-terracotta"
                                    data-testid={`repeat-${rec.type}-interval`}
                                />
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {helper && (
                <p
                    className="font-body text-[11px] text-warm-500 mt-2 leading-snug"
                    data-testid="repeat-picker-helper"
                >
                    {helper}
                </p>
            )}
        </div>
    );
}

function clamp(n, lo, hi) {
    if (Number.isNaN(n)) return lo;
    return Math.max(lo, Math.min(hi, n));
}

function nth(n) {
    if (n % 100 >= 11 && n % 100 <= 13) return `${n}th`;
    switch (n % 10) {
        case 1: return `${n}st`;
        case 2: return `${n}nd`;
        case 3: return `${n}rd`;
        default: return `${n}th`;
    }
}
